import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Undo2, Redo2, Loader2, History as HistoryIcon, ChevronRight, Circle, CircleDot } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_STYLES = {
  create:      { label: "CREATE", cls: "bg-emerald-100 text-emerald-700" },
  update:      { label: "UPDATE", cls: "bg-sky-100 text-sky-700" },
  delete:      { label: "DELETE", cls: "bg-red-100 text-red-700" },
  bulk_update: { label: "BULK U", cls: "bg-sky-100 text-sky-700" },
  bulk_delete: { label: "BULK D", cls: "bg-red-100 text-red-700" },
  lock:        { label: "LOCK",   cls: "bg-amber-100 text-amber-700" },
  unlock:      { label: "UNLOCK", cls: "bg-amber-100 text-amber-700" },
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

/**
 * Floating Undo / Redo buttons + Photoshop-style History dropdown — for
 * admin/owner users. Polls pending counts every 30s.
 */
export default function UndoFab() {
  const [pending, setPending] = useState(0);
  const [pendingRedo, setPendingRedo] = useState(0);
  const [busy, setBusy] = useState(false);
  const [busyRedo, setBusyRedo] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [stack, setStack] = useState([]);
  const [cursorId, setCursorId] = useState(null);
  const [stackLoading, setStackLoading] = useState(false);
  const [jumping, setJumping] = useState(null);
  const stackRef = useRef(null);
  const navigate = useNavigate();

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const role = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}").role; } catch { return null; }
  })();
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const fetchPending = useCallback(async () => {
    if (!token || (role !== "admin" && role !== "owner")) return;
    try {
      const r = await axios.get(`${API}/audit-log?limit=1&only_pending=true`, authHeader);
      setPending(r.data.pending_reversible || 0);
      setPendingRedo(r.data.pending_redo || 0);
    } catch { /* ignore */ }
  }, [token, role]);

  const fetchStack = useCallback(async () => {
    if (!token || (role !== "admin" && role !== "owner")) return;
    setStackLoading(true);
    try {
      const r = await axios.get(`${API}/audit-log/history-stack?limit=12`, authHeader);
      setStack(r.data.stack || []);
      setCursorId(r.data.cursor_id || null);
    } catch (e) {
      toast.error("Failed to load history");
    } finally { setStackLoading(false); }
  }, [token, role]);

  useEffect(() => {
    fetchPending();
    const i = setInterval(fetchPending, 30000);
    return () => clearInterval(i);
  }, [fetchPending]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (historyOpen && stackRef.current && !stackRef.current.contains(e.target)) {
        setHistoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [historyOpen]);

  if (!token || (role !== "admin" && role !== "owner")) return null;

  const handleUndo = async () => {
    if (!window.confirm("Undo the most recent reversible action?")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/audit-log/undo-last`, {}, authHeader);
      toast.success(r.data.summary ? `Undone: ${r.data.summary}` : "Action undone");
      fetchPending();
      if (historyOpen) fetchStack();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nothing to undo");
    } finally { setBusy(false); }
  };

  const handleRedo = async () => {
    setBusyRedo(true);
    try {
      const r = await axios.post(`${API}/audit-log/redo-last`, {}, authHeader);
      toast.success(r.data.summary ? `Redone: ${r.data.summary}` : "Action redone");
      fetchPending();
      if (historyOpen) fetchStack();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nothing to redo");
    } finally { setBusyRedo(false); }
  };

  const openHistory = () => {
    setHistoryOpen((prev) => {
      const next = !prev;
      if (next) fetchStack();
      return next;
    });
  };

  const jumpTo = async (id) => {
    if (id === cursorId) return;
    setJumping(id);
    try {
      const r = await axios.post(`${API}/audit-log/jump-to/${id}`, {}, authHeader);
      const parts = [];
      if (r.data.undone) parts.push(`${r.data.undone} undone`);
      if (r.data.redone) parts.push(`${r.data.redone} redone`);
      toast.success(parts.length ? `Jumped — ${parts.join(", ")}` : "Cursor moved");
      await Promise.all([fetchStack(), fetchPending()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Jump failed");
    } finally { setJumping(null); }
  };

  return (
    <div
      ref={stackRef}
      className="fixed z-40 flex items-end gap-2"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)",
        right: "calc(env(safe-area-inset-right, 0px) + 1.25rem)",
      }}
      data-testid="undo-fab-wrap"
    >
      {/* History dropdown panel */}
      {historyOpen && (
        <div
          className="absolute bottom-full right-0 mb-2 w-[380px] max-w-[92vw] max-h-[70vh] bg-white/95 backdrop-blur border border-slate-200 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          data-testid="history-stack-panel"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
            <div className="flex items-center gap-2">
              <HistoryIcon className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-xs font-semibold text-slate-700">History</span>
              <span className="text-[10px] text-slate-400">— click a step to jump</span>
            </div>
            <button
              onClick={() => { fetchStack(); }}
              className="text-[10px] text-slate-500 hover:text-slate-700"
              data-testid="history-refresh"
            >Refresh</button>
          </div>
          <div className="overflow-y-auto">
            {stackLoading && stack.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-slate-400 text-xs gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : stack.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-xs">No history yet</div>
            ) : stack.map((row) => {
              const isCursor = row.id === cursorId;
              const isUndone = row.state === "undone";
              const s = ACTION_STYLES[row.action] || { label: (row.action || "").toUpperCase(), cls: "bg-slate-100 text-slate-600" };
              return (
                <button
                  key={row.id}
                  onClick={() => jumpTo(row.id)}
                  disabled={jumping !== null}
                  data-testid={`history-step-${row.id}`}
                  className={`w-full flex items-start gap-2 px-4 py-2.5 border-b border-slate-100 last:border-b-0 text-left transition-colors ${
                    isCursor
                      ? "bg-violet-50/60 hover:bg-violet-50"
                      : isUndone
                        ? "opacity-50 hover:opacity-100 hover:bg-slate-50"
                        : "hover:bg-slate-50"
                  } ${jumping === row.id ? "animate-pulse" : ""}`}
                >
                  <div className="pt-1 shrink-0">
                    {isCursor ? (
                      <CircleDot className="w-3.5 h-3.5 text-violet-600" />
                    ) : (
                      <Circle className={`w-3.5 h-3.5 ${isUndone ? "text-slate-300" : "text-slate-400"}`} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`inline-block text-[8px] font-semibold leading-none px-1 py-0.5 rounded ${s.cls}`}>
                        {s.label}
                      </span>
                      <span className="text-[10px] text-slate-400">{row.collection}</span>
                      <span className="text-[10px] text-slate-300 ml-auto">{timeAgo(row.timestamp)}</span>
                    </div>
                    <p className={`text-[11px] mt-0.5 ${isUndone ? "text-slate-400 line-through" : "text-slate-700"} ${isCursor ? "font-semibold" : ""}`}>
                      {row.summary || row.action}
                    </p>
                  </div>
                  {isCursor && (
                    <span className="text-[9px] font-semibold text-violet-600 uppercase pt-1 shrink-0">now</span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="border-t border-slate-100 px-4 py-2 flex items-center justify-between bg-slate-50/60">
            <span className="text-[10px] text-slate-500">Showing last {stack.length}</span>
            <button
              onClick={() => { setHistoryOpen(false); navigate("/audit-log"); }}
              className="text-[10px] text-violet-600 hover:text-violet-800 font-medium flex items-center gap-0.5"
              data-testid="history-open-full"
            >Full audit log <ChevronRight className="w-3 h-3" /></button>
          </div>
        </div>
      )}

      {/* Buttons row */}
      <button
        onClick={openHistory}
        className={`text-[11px] backdrop-blur border px-3 py-2 rounded-full shadow-sm transition-all flex items-center gap-1 ${historyOpen ? "bg-white text-slate-800 border-slate-300" : "text-slate-500 bg-white/80 border-slate-200 hover:bg-white hover:text-slate-700"}`}
        data-testid="undo-fab-history"
        title="Open history stack"
      >
        <HistoryIcon className="w-3.5 h-3.5" />
        History
      </button>
      <button
        onClick={handleUndo}
        disabled={busy || pending === 0}
        data-testid="undo-fab"
        title={pending === 0 ? "No reversible actions" : "Undo last action"}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg transition-all text-white text-xs font-medium ${pending === 0 ? "bg-slate-300 cursor-not-allowed" : "bg-violet-600 hover:bg-violet-700 hover:shadow-xl"}`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
        Undo Last
        {pending > 0 && <span className="bg-white/25 px-1.5 py-0.5 rounded-full text-[10px] font-bold">{pending}</span>}
      </button>
      <button
        onClick={handleRedo}
        disabled={busyRedo || pendingRedo === 0}
        data-testid="redo-fab"
        title={pendingRedo === 0 ? "Nothing to redo" : "Redo last undone action"}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg transition-all text-white text-xs font-medium ${pendingRedo === 0 ? "bg-slate-300 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700 hover:shadow-xl"}`}
      >
        {busyRedo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Redo2 className="w-4 h-4" />}
        Redo
        {pendingRedo > 0 && <span className="bg-white/25 px-1.5 py-0.5 rounded-full text-[10px] font-bold">{pendingRedo}</span>}
      </button>
    </div>
  );
}
